import { config, useSpring } from '@react-spring/three';
import { useThree } from '@react-three/fiber';
import { Bloom, EffectComposer, Vignette } from '@react-three/postprocessing';
import { ReactNode, useEffect } from 'react';

import { AdaptativeQuality, useHighQuality } from 'atoms/AdaptativeQuality';
import { GroundEnvironment } from 'atoms/GroundEnvironment';
import { Model } from 'atoms/Model';
import { ModifyMaterial } from 'atoms/ModifyMaterial';
import { Pedestal } from 'atoms/Pedestal';
import { SafeCanvas } from 'atoms/SafeCanvas';
import { SceneLights } from 'atoms/SceneLights';
import { FadeProvider } from 'contexts/Fade';

import factoryRevalEnvNeonChristmasUrl from './assets/factoryRevalEnvNeonChristmas.jpg';
import factoryRevealEnvUrl from './assets/factoryRevealEnv.jpg';
import footballFactoryBoxEnvUrl from './assets/football-factoryBoxEnv.jpg';
import footballfactoryCardEnvUrl from './assets/football-factoryCardEnv.jpg';
import nbaFactoryBoxEnvUrl from './assets/nba-factoryBoxEnv.jpg';
import nbafactoryCardEnvUrl from './assets/nba-factoryCardEnv.jpg';
// eslint-disable-next-line import/no-unresolved
import printerUrl from './assets/printer.gltf?url';

enum Sport {
  BASEBALL = 'BASEBALL',
  FOOTBALL = 'FOOTBALL',
  NBA = 'NBA',
}

const factoryCardEnvUrls = {
  [Sport.FOOTBALL]: footballfactoryCardEnvUrl,
  [Sport.NBA]: nbafactoryCardEnvUrl,
  [Sport.BASEBALL]: nbafactoryCardEnvUrl,
};

const factoryBoxEnvUrls = {
  [Sport.FOOTBALL]: footballFactoryBoxEnvUrl,
  [Sport.NBA]: nbaFactoryBoxEnvUrl,
  [Sport.BASEBALL]: nbaFactoryBoxEnvUrl,
};

type Props = {
  children: ReactNode;
  context: 'box' | 'card' | 'reveal';
  fallback?: ReactNode;
  sport?: Sport;
};

const FactoryBoxContext = ({ children }: { children: ReactNode }) => {
  return <Pedestal>{children}</Pedestal>;
};

const CardPrinter = ({ children }: { children: ReactNode }) => {
  return (
    <>
      <group scale={3} position-x={2.8} position-y={0}>
        <Model src={printerUrl} castShadow receiveShadow>
          <ModifyMaterial
            emissiveIntensity={10}
            attach="children-0-children-0-children-3-material"
          />
        </Model>
      </group>
      <group position-y={2.2}>{children}</group>
    </>
  );
};

const FactoryCardContext = ({ children }: { children: ReactNode }) => {
  return <CardPrinter>{children}</CardPrinter>;
};

const ZoomOnMobile = () => {
  const data = useThree();

  const {
    camera,
    size: { width },
  } = data;
  const [, api] = useSpring(() => ({
    from: { position: [0, 2, 4] as [number, number, number] },
    onChange: ({ value: { position } }) => {
      camera.position.set(...(position as [number, number, number]));
    },
    config: config.slow,
  }));

  useEffect(() => {
    if (width < 800) {
      api.start({ position: [0, 2.25, 3] });
    } else {
      api.start({ position: [0, 2, 4] });
    }
  }, [api, width]);

  return null;
};

const FactoryContent = ({
  children,
  context,
  sport = Sport.FOOTBALL,
}: Props) => {
  const FactoryContext =
    context === 'card' ? FactoryCardContext : FactoryBoxContext;

  const hasNeonChristmasStarted =
    Date.now() > Date.parse('2024-12-01T11:00:00+01:00');
  const envProps = {
    box: { url: factoryBoxEnvUrls[sport], 'rotation-y': Math.PI / -2 },
    card: { url: factoryCardEnvUrls[sport], 'rotation-y': Math.PI / -3 },
    reveal: {
      url: hasNeonChristmasStarted
        ? factoryRevalEnvNeonChristmasUrl
        : factoryRevealEnvUrl,
      radius: 8,
      height: 2,
      'rotation-y': Math.PI / 4,
    },
  }[context];
  const highQuality = useHighQuality();
  return (
    <>
      {highQuality ? (
        <SceneLights color={0xffffff} intensity={0.25}>
          <directionalLight position={[-5, 5, 5]} intensity={1.5 * Math.PI} />
        </SceneLights>
      ) : (
        <directionalLight position={[-5, 5, 5]} intensity={1.5 * Math.PI} />
      )}
      {context === 'reveal' && (
        <>
          <ZoomOnMobile />
          <directionalLight position={[5, 5, 5]} intensity={Math.PI} />
        </>
      )}
      <FactoryContext>{children}</FactoryContext>
      <GroundEnvironment height={2} radius={8} {...envProps} />
    </>
  );
};

export const Factory = ({
  children,
  context,
  sport,
  fallback = null,
}: Props) => {
  return (
    <SafeCanvas
      resize={{ scroll: false }}
      shadows
      camera={{
        fov: 75,
        position: [0, 2, 4],
        rotation: [0, 0, 0],
      }}
      style={{
        touchAction: 'none',
      }}
      fallback={fallback}
    >
      <AdaptativeQuality>
        <FadeProvider>
          <FactoryContent context={context} sport={sport}>
            {children}
          </FactoryContent>
        </FadeProvider>
      </AdaptativeQuality>

      <EffectComposer>
        <Bloom
          intensity={0.25}
          mipmapBlur
          radius={0.4}
          luminanceThreshold={2}
          luminanceSmoothing={1}
        />
        <Vignette eskil={false} offset={0.1} darkness={0.5} />
      </EffectComposer>
    </SafeCanvas>
  );
};

export default Factory;
